import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { GetApp as DownloadIcon } from "@mui/icons-material";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import httpRequest from "../utils/httpRequest";

const CustomerCard = ({
  customer,
  handleViewTransactions,
  setActiveCustomer,
  setAddPaymentOpen,
  fetchCustomerData,
  selectedFY
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({ ...customer });
  const [serviceLocations, setServiceLocations] = useState([]);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setFormValues({ ...customer });
  }, [customer]);

  useEffect(() => {
    const fetchServiceLocations = async () => {
      try {
        const data = await httpRequest("service-location/", "GET");
        setServiceLocations(data || []);
      } catch (error) {
        console.error("Error fetching service locations:", error);
      }
    };

    fetchServiceLocations();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'mobile') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.slice(0, 10);
      setFormValues((prev) => ({
        ...prev,
        [name]: limitedDigits,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = async () => {
    // Validate mobile number before saving
    if (formValues.mobile && formValues.mobile.length !== 10) {
      setSnackbar({
        open: true,
        message: 'Mobile number must be exactly 10 digits',
        severity: 'error'
      });
      return;
    }

    try {
      const payload = {
        ...formValues,
        customer_id: customer.customer_id,
      };
  
      await httpRequest("customers/update", "POST", payload);
      setIsEditing(false);
      fetchCustomerData();
      setSnackbar({
        open: true,
        message: 'Customer updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error("Failed to update customer:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update customer',
        severity: 'error'
      });
    }
  };

  const handleSendWhatsappStatement = async () => {
    if (!customer.mobile || customer.mobile === 'NA' || customer.mobile.trim() === '') {
      setSnackbar({
        open: true,
        message: "Customer mobile number not found or invalid. Please update the customer's mobile number first.",
        severity: 'error'
      });
      return;
    }

    setLoadingWhatsapp(true);
    try {
      // Check WhatsApp status first
      const whatsappStatus = await httpRequest('whatsapp/status', 'GET');
      if (!whatsappStatus.isReady) {
        throw new Error("WhatsApp is not connected. Please scan the QR code in the backend terminal.");
      }

      // Generate PDF statement
      const pdfBlob = await httpRequest(
        `customers/transactions/${customer.customer_id}?page=1&limit=1000000000&fy=${selectedFY}&action=download`,
        "GET",
        null,
        null,
        "blob"
      );

      // Prepare form data
      const formData = new FormData();
      formData.append('pdfFile', pdfBlob, `${customer.name}_statement.pdf`);
      formData.append('recipient', customer.mobile);
      formData.append('fileName', `${customer.name}_statement.pdf`);
      const fyText = selectedFY === 'all' ? 'till date' : `for FY ${selectedFY}`;
      formData.append('message', `Dear ${customer.name}, please find your account statement ${fyText} attached.`);
      formData.append('customerId', customer.customer_id);

      // Send to WhatsApp
      const response = await httpRequest('whatsapp/sendStatement', 'POST', formData, null, 'json', true);
      
      setSnackbar({
        open: true,
        message: 'Statement sent via WhatsApp successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error sending WhatsApp statement:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || "Failed to send statement via WhatsApp",
        severity: 'error'
      });
    } finally {
      setLoadingWhatsapp(false);
      // Close the menu when API call finishes (success or error)
      setMenuOpen(false);
      setMenuAnchorEl(null);
    }
  };

  const balancedColors = [
    "#1E3A8A",
    "#D97706",
    "#4B5563",
    "#9333EA",
    "#F59E0B",
    "#10B981",
    "#EF4444",
    "#e6a40b",
    "#14B8A6",
    "#3B82F6",
    "#05bbf2",
    "#6EE7B7",
    "#bdd108",
    "#F43F5E",
    "#9D174D",
    "#4B5563",
    "#B91C1C",
    "#8B5CF6",
    "#0F766E",
    "#B91C1C",
    "#6366F1",
    "#eb0e4d",
    "#EC4899",
    "#16A34A",
    "#F472B6",
    "#1D4ED8",
    "#F97316",
  ];

  const getAvatarColor = (name) => {
    const charCode = name.toLowerCase().charCodeAt(0) - 97;
    return balancedColors[charCode % balancedColors.length];
  };

  const isValidMobileNumber = (mobile) => {
    if (!mobile || mobile === 'NA' || mobile.trim() === '') return false;
    const cleanMobile = mobile.replace(/\D/g, ''); // Remove non-digits
    return cleanMobile.length >= 10;
  };

  return (
    <Grid
      key={customer.customer_id}
      size={{
        xs: 12,
        md: 4
      }}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: getAvatarColor(customer.name) }}>
                {customer.name[0]}
              </Avatar>

              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary"
                >
                  {customer.name} (#{customer.account_number})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GST: {customer.gst_number}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" gap={1}>
                {customer.transactionAvailable && (
                  <Tooltip title="View transaction history" arrow>
                    <IconButton
                      onClick={() => handleViewTransactions(customer)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Add customer payment" arrow>
                  <IconButton
                    onClick={() => {
                      setActiveCustomer(customer);
                      setAddPaymentOpen(true);
                    }}
                    size="small"
                  >
                    <PaymentIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box display="flex" gap={1}>
                {customer.transactionAvailable && (
                  <Tooltip title="Statement actions" arrow>
                    <IconButton
                      onClick={(e) => {
                        setMenuAnchorEl(e.currentTarget);
                        setMenuOpen(true);
                      }}
                      size="small"
                      disabled={loadingWhatsapp}
                    >
                      {loadingWhatsapp ? (
                        <CircularProgress size={16} />
                      ) : (
                        <MoreVertIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Edit customer" arrow>
                  <IconButton onClick={() => setIsEditing(true)} size="small">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {isEditing ? (
            <Box
              component="form"
              noValidate
              autoComplete="off"
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <TextField
                label="Name"
                name="name"
                value={formValues.name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Account Number"
                name="account_number"
                value={formValues.account_number}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="GST Number"
                name="gst_number"
                value={formValues.gst_number}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Mobile"
                name="mobile"
                value={formValues.mobile}
                onChange={handleChange}
                fullWidth
                size="small"
                helperText={formValues.mobile && formValues.mobile.length !== 10 ? "Mobile number must be exactly 10 digits" : ""}
                error={formValues.mobile && formValues.mobile.length !== 10}
                inputProps={{
                  maxLength: 10,
                  pattern: "[0-9]*"
                }}
              />
              <TextField
                label="Email"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Address"
                name="address"
                value={formValues.address}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Service Location</InputLabel>
                <Select
                  name="location"
                  value={formValues.location || ""}
                  onChange={handleChange}
                  label="Service Location"
                >
                  {serviceLocations.map((location) => (
                    <MenuItem key={location.id} value={location.location}>
                      {location.location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" justifyContent="space-between" mt={1}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    setFormValues({ ...customer });
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary">
                    Mobile
                  </Typography>
                  <Typography variant="body2">{customer.mobile}</Typography>
                </Grid>
                <Grid size={8}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{customer.email}</Typography>
                </Grid>
                <Grid size={8}>
                  <Typography variant="caption" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body2">{customer.address}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary">
                    Service Location
                  </Typography>
                  <Typography variant="body2">{customer.location}</Typography>
                </Grid>
                <Grid size={12}>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    {customer.last_payment_date ? (
                      <>
                        <PaymentIcon sx={{ fontSize: 16, color: 'action.active', mr: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                          Last Payment: <span style={{ color: '#222', fontWeight: 500 }}>
                            {new Date(customer.last_payment_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                          </span> (₹{customer.last_payment_amount?.toFixed(2)})
                        </Typography>
                      </>
                    ) : (
                      <>
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'action.disabled', mr: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                          No payment this year
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="error.dark">
                  Debit: ₹{customer.total_debit.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="green">
                  Credit: ₹{customer.total_credit.toFixed(2)}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  Balance: ₹{customer.balance.toFixed(2)}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Statement Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={() => {
          setMenuOpen(false);
          setMenuAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            handleViewTransactions(customer, "download");
            setMenuOpen(false);
            setMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Statement</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSendWhatsappStatement();
            // Don't close menu during API call - let user manually close
          }}
          disabled={loadingWhatsapp || !isValidMobileNumber(customer.mobile)}
        >
          <ListItemIcon>
            {loadingWhatsapp ? (
              <CircularProgress size={16} />
            ) : (
              <WhatsAppIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {loadingWhatsapp ? 'Sending...' : 'Send via WhatsApp'}
          </ListItemText>
        </MenuItem>
      </Menu>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default CustomerCard;